import React from "react";
import { ArrowRight, Menu as MenuIcon, X } from "lucide-react";

const ONBOARDING_PENDING_KEY = "sf_header_onboarding_pending_user";
const ONBOARDING_DONE_PREFIX = "sf_header_onboarding_done_";
const MESSAGE_ONBOARDING_DONE_PREFIX = "sf_message_onboarding_done_";
const MOBILE_BREAKPOINT = 1280;

const DESKTOP_STEPS = [
  {
    id: "home",
    selector: '[data-testid="nav-home"], [data-testid="logo-link"]',
    title: "Accueil",
    text: "Reviens ici pour retrouver la page principale, les promesses fortes de SF Learning et les accès rapides.",
  },
  {
    id: "catalog",
    selector: '[data-testid="nav-catalog"]',
    title: "Formations",
    text: "Découvre les programmes disponibles. Si une nouveauté arrive, un badge apparaît puis disparaît quand tu ouvres ce menu.",
  },
  {
    id: "blog",
    selector: '[data-testid="nav-blog"]',
    title: "Blog",
    text: "Lis les articles, conseils et contenus pédagogiques pour progresser même entre deux modules.",
  },
  {
    id: "community",
    selector: '[data-testid="nav-community"]',
    title: "Communauté",
    text: "Accède à la landing communauté. L’espace privé est réservé aux membres qui ont au moins une formation active.",
  },
  {
    id: "affiliate",
    selector: '[data-testid="nav-affiliate"]',
    title: "Parrainage",
    text: "Retrouve ton lien, ton code, tes filleuls, tes commissions et ton portefeuille de parrainage.",
  },
  {
    id: "messages",
    selector: '[data-testid="messages-link"]',
    title: "Messages",
    text: "Échange en privé avec les membres. Un badge s’affiche quand tu reçois un nouveau message.",
  },
  {
    id: "dashboard",
    selector: '[data-testid="dashboard-link"]',
    title: "Mon espace",
    text: "C’est ton tableau de bord personnel : formations suivies, progression, profil et accès importants.",
  },
];

const MOBILE_STEPS = [
  {
    id: "home",
    selector: '[data-testid="mobile-nav-home"]',
    title: "Accueil",
    text: "Reviens ici pour retrouver la page principale, les promesses fortes de SF Learning et les accès rapides.",
  },
  {
    id: "catalog",
    selector: '[data-testid="mobile-nav-catalog"]',
    title: "Formations",
    text: "Découvre les programmes disponibles. Si une nouveauté arrive, un badge apparaît puis disparaît quand tu ouvres ce menu.",
  },
  {
    id: "blog",
    selector: '[data-testid="mobile-nav-blog"]',
    title: "Blog",
    text: "Lis les articles, conseils et contenus pédagogiques pour progresser même entre deux modules.",
  },
  {
    id: "community",
    selector: '[data-testid="mobile-nav-community"]',
    title: "Communauté",
    text: "Accède à la landing communauté. L’espace privé est réservé aux membres qui ont au moins une formation active.",
  },
  {
    id: "affiliate",
    selector: '[data-testid="mobile-nav-affiliate"]',
    title: "Parrainage",
    text: "Retrouve ton lien, ton code, tes filleuls, tes commissions et ton portefeuille de parrainage.",
  },
  {
    id: "messages",
    selector: '[data-testid="mobile-nav-messages"]',
    title: "Messages",
    text: "Échange en privé avec les membres. Un badge s’affiche quand tu reçois un nouveau message.",
  },
  {
    id: "dashboard",
    selector: '[data-testid="mobile-nav-dashboard"]',
    title: "Mon espace",
    text: "C’est ton tableau de bord personnel : formations suivies, progression, profil et accès importants.",
  },
];

const ADMIN_DESKTOP_STEP = {
  id: "admin",
  selector: '[data-testid="admin-link"]',
  title: "Admin",
  text: "Ton espace d’administration pour piloter les formations, apprenants, emails, textes du site et réglages.",
};

const ADMIN_MOBILE_STEP = {
  id: "admin",
  selector: '[data-testid="mobile-nav-admin"]',
  title: "Admin",
  text: "Ton espace d’administration pour piloter les formations, apprenants, emails, textes du site et réglages.",
};

const MOBILE_MENU_STEP = {
  id: "mobile-menu",
  selector: '[data-testid="mobile-menu-toggle"]',
  title: "Menu mobile",
  text: "Sur téléphone, tout se trouve dans ce menu. Ouvre-le pour découvrir les raccourcis SF Learning.",
};

function isBrowser() {
  return typeof window !== "undefined";
}

function isMobileViewport() {
  return isBrowser() && window.innerWidth < MOBILE_BREAKPOINT;
}

function getDoneKey(userId) {
  return `${ONBOARDING_DONE_PREFIX}${userId}`;
}

function getMessageDoneKey(userId) {
  return `${MESSAGE_ONBOARDING_DONE_PREFIX}${userId}`;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getTargetRect(selector) {
  if (!isBrowser() || !selector) return null;
  const target = document.querySelector(selector);
  if (!target) return null;
  const rect = target.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return rect;
}

export default function HeaderOnboarding({ user, mobileMenuOpen = false, openMobileMenu, canAccessMessages = false }) {
  const [visible, setVisible] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [rect, setRect] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(false);
  const [messagesOnlyMode, setMessagesOnlyMode] = React.useState(false);

  React.useEffect(() => {
    if (!isBrowser()) return undefined;

    const updateViewportMode = () => setIsMobile(isMobileViewport());
    updateViewportMode();

    window.addEventListener("resize", updateViewportMode);
    return () => window.removeEventListener("resize", updateViewportMode);
  }, []);

  const steps = React.useMemo(() => {
    const baseSteps = isMobile ? MOBILE_STEPS : DESKTOP_STEPS;
    const adminStep = isMobile ? ADMIN_MOBILE_STEP : ADMIN_DESKTOP_STEP;
    const filteredBaseSteps = canAccessMessages
      ? baseSteps
      : baseSteps.filter((step) => step.id !== "messages");
    const visibleSteps = user?.role === "admin" ? [...filteredBaseSteps, adminStep] : filteredBaseSteps;

    if (isMobile && !mobileMenuOpen) {
      return [MOBILE_MENU_STEP];
    }

    return visibleSteps;
  }, [isMobile, mobileMenuOpen, user?.role, canAccessMessages]);

  const onboardingSteps = messagesOnlyMode ? steps.filter((step) => step.id === "messages") : steps;

  const currentStep = onboardingSteps[stepIndex] || onboardingSteps[0];

  React.useEffect(() => {
    if (!isBrowser() || !user?.user_id) return;

    const done = window.localStorage.getItem(getDoneKey(user.user_id));
    const pendingUserId = window.localStorage.getItem(ONBOARDING_PENDING_KEY);
    const messageGuideDone = window.localStorage.getItem(getMessageDoneKey(user.user_id));
    const hadFormationBefore = window.localStorage.getItem(`sf_had_formation_${user.user_id}`) === "true";

    if (!done && pendingUserId === user.user_id) {
      setMessagesOnlyMode(false);
      setVisible(true);
      setStepIndex(0);
      return;
    }

    if (user?.has_active_formation === true && !hadFormationBefore) {
      window.localStorage.setItem(`sf_had_formation_${user.user_id}`, "true");

      if (!messageGuideDone && canAccessMessages) {
        setMessagesOnlyMode(true);
        setVisible(true);
        setStepIndex(0);
      }
    }
  }, [user?.user_id, user?.has_active_formation, canAccessMessages]);

  React.useEffect(() => {
    if (!visible || !currentStep) return undefined;

    const updateRect = () => setRect(getTargetRect(currentStep.selector));
    updateRect();

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [visible, currentStep, mobileMenuOpen]);

  React.useEffect(() => {
    if (!visible) return;
    setStepIndex(0);
  }, [isMobile]);  // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!visible || !isMobile) return;
    if (mobileMenuOpen) setStepIndex(0);
  }, [visible, isMobile, mobileMenuOpen]);

  const complete = React.useCallback(() => {
    if (isBrowser() && user?.user_id) {
      if (messagesOnlyMode) {
        window.localStorage.setItem(getMessageDoneKey(user.user_id), new Date().toISOString());
      } else {
        window.localStorage.setItem(getDoneKey(user.user_id), new Date().toISOString());
        window.localStorage.removeItem(ONBOARDING_PENDING_KEY);
      }
    }
    setVisible(false);
  }, [user?.user_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const next = () => {
    if (isMobile && !mobileMenuOpen) {
      openMobileMenu?.();
      return;
    }

    if (stepIndex >= onboardingSteps.length - 1) {
      complete();
      return;
    }
    setStepIndex((prev) => prev + 1);
  };

  if (!visible || !currentStep) return null;

  const cardWidth = isMobile ? Math.min(340, Math.max(280, window.innerWidth - 32)) : 360;
  const margin = 16;
  const viewportWidth = isBrowser() ? window.innerWidth : 1200;
  const viewportHeight = isBrowser() ? window.innerHeight : 800;

  const fallbackStyle = {
    left: "50%",
    top: isMobile ? "96px" : "120px",
    transform: "translateX(-50%)",
  };

  let cardStyle = fallbackStyle;
  if (rect) {
    const preferredLeft = rect.left + rect.width / 2 - cardWidth / 2;
    const left = clamp(preferredLeft, margin, Math.max(margin, viewportWidth - cardWidth - margin));
    const belowTop = rect.bottom + 14;
    const aboveTop = rect.top - 238;
    const preferredTop = belowTop + 230 < viewportHeight ? belowTop : Math.max(margin, aboveTop);
    const top = clamp(preferredTop, margin, Math.max(margin, viewportHeight - 245));
    cardStyle = { left: `${left}px`, top: `${top}px` };
  }

  const spotlightPadding = isMobile ? 8 : 10;
  const spotlightRadius = 18;
  const spotlight = rect
    ? {
        x: Math.max(0, rect.left - spotlightPadding),
        y: Math.max(0, rect.top - spotlightPadding),
        width: Math.min(viewportWidth, rect.width + spotlightPadding * 2),
        height: Math.min(viewportHeight, rect.height + spotlightPadding * 2),
      }
    : null;

  const nextLabel = isMobile && !mobileMenuOpen
    ? "Ouvrir le menu"
    : stepIndex >= steps.length - 1
      ? "Terminer"
      : "Suivant";

  return (
    <div className="fixed inset-0 z-[80] pointer-events-none" aria-live="polite">
      {spotlight ? (
        <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
          <defs>
            <mask id="sf-header-onboarding-spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={spotlight.x}
                y={spotlight.y}
                width={spotlight.width}
                height={spotlight.height}
                rx={spotlightRadius}
                ry={spotlightRadius}
                fill="black"
              />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.58)"
            mask="url(#sf-header-onboarding-spotlight-mask)"
          />
        </svg>
      ) : (
        <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
      )}

      {spotlight && (
        <div
          className="absolute rounded-[18px] border-2 border-brand-orange bg-white/10 shadow-[0_0_35px_rgba(244,161,0,0.75)] ring-4 ring-white/40 transition-all duration-200"
          style={{
            left: `${spotlight.x}px`,
            top: `${spotlight.y}px`,
            width: `${spotlight.width}px`,
            height: `${spotlight.height}px`,
          }}
          aria-hidden="true"
        />
      )}

      <div
        className="pointer-events-auto fixed w-[calc(100vw-2rem)] max-w-[360px] rounded-3xl border border-emerald-100 bg-white p-5 shadow-2xl"
        style={cardStyle}
        role="dialog"
        aria-label="Guide rapide SF Learning"
      >
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <p className="label mb-1">Guide rapide</p>
            <h3 className="font-heading text-2xl font-black leading-tight">{currentStep.title}</h3>
          </div>
          <button
            type="button"
            onClick={complete}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-black"
            aria-label="Quitter le guide"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm leading-relaxed text-gray-600">{currentStep.text}</p>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={complete}
            className="text-sm font-semibold text-gray-500 transition hover:text-black"
          >
            Quitter
          </button>

          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
              {messagesOnlyMode ? "1/1" : (isMobile && !mobileMenuOpen ? "1/2" : `${stepIndex + 1}/${onboardingSteps.length}`)}
            </span>
            <button type="button" onClick={next} className="btn-primary px-5 py-3">
              {nextLabel}
              {isMobile && !mobileMenuOpen ? <MenuIcon className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { ONBOARDING_PENDING_KEY };
